import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { Radio, ArrowUp, ArrowDown, ArrowUpDown, CheckCircle2, AlertTriangle, Minus } from 'lucide-react';
import { SimStatusBadge } from './SimStatusBadge';
import type { SimCard } from '../types';
import type { VolumeTier } from '../utils/pricing';
import { getSimMonthlyCost, formatCost } from '../utils/pricing';

/**
 * Format data size from MB to human readable format
 */
function formatDataSize(mb: number): string {
  if (mb < 1) {
    const kb = mb * 1024;
    return `${kb.toLocaleString(undefined, { maximumFractionDigits: 2 })} KB`;
  }
  if (mb < 1024) {
    return `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;
  }
  if (mb < 1024 * 1024) {
    const gb = mb / 1024;
    return `${gb.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
  }
  const tb = mb / (1024 * 1024);
  return `${tb.toLocaleString(undefined, { maximumFractionDigits: 2 })} TB`;
}

export type SortKey = 'iccid' | 'status' | 'customer' | 'account' | 'gateway' | 'rate_plan' | 'usage' | 'cost' | 'lastSyncAt';

interface SimInventoryTableProps {
  simCards: SimCard[];
  sortKey?: SortKey;
  sortDirection?: 1 | -1;
  onSort?: (key: SortKey) => void;
  volumeTier?: VolumeTier;
  onRowClick?: (simCardId: string) => void;
}

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  currentSortKey?: SortKey;
  sortDirection?: 1 | -1;
  onSort?: (key: SortKey) => void;
  className?: string;
}

function SortableHeader({ label, sortKey, currentSortKey, sortDirection, onSort, className }: SortableHeaderProps) {
  const isActive = currentSortKey === sortKey;

  return (
    <TableHead
      className={`cursor-pointer select-none hover:bg-muted/50 ${className || ''}`}
      onClick={() => onSort?.(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isActive ? (
          sortDirection === 1 ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
        )}
      </div>
    </TableHead>
  );
}

export function SimInventoryTable({ simCards, sortKey, sortDirection, onSort, volumeTier = 'tier2', onRowClick }: SimInventoryTableProps) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ICCID</TableHead>
              <SortableHeader
                label="Status"
                sortKey="status"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Account"
                sortKey="customer"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Account"
                sortKey="account"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <TableHead className="w-[80px]">Contract</TableHead>
              <SortableHeader
                label="Gateway"
                sortKey="gateway"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Rate Plan"
                sortKey="rate_plan"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Usage"
                sortKey="usage"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
                className="text-right"
              />
              <SortableHeader
                label="Cost"
                sortKey="cost"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
                className="text-right"
              />
              <SortableHeader
                label="Last Sync"
                sortKey="lastSyncAt"
                currentSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {simCards.map((sim) => (
              <TableRow
                key={sim.id}
                className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                onClick={() => onRowClick?.(sim.id)}
              >
                <TableCell className="font-mono text-sm">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">
                          {formatIccid(sim.iccid)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="space-y-1 text-xs">
                          <p><strong>ICCID:</strong> {sim.iccid}</p>
                          {sim.imei && <p><strong>IMEI:</strong> {sim.imei}</p>}
                          {sim.imsi && <p><strong>IMSI:</strong> {sim.imsi}</p>}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <SimStatusBadge status={sim.status} />
                    {sim.jasper_status && sim.jasper_status !== sim.status && (
                      <span className="text-xs text-muted-foreground">
                        ({sim.jasper_status})
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help truncate max-w-[150px] block">
                          {sim.jasper_customer || '-'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{sim.jasper_customer || 'No customer assigned'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  {sim.account_name ? (
                    <div className="flex flex-col">
                      <span className="font-medium">{sim.account_name}</span>
                      {sim.account_number && (
                        <span className="text-xs text-muted-foreground">
                          #{sim.account_number}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-orange-500 text-sm">Unassigned</span>
                  )}
                </TableCell>
                <TableCell>
                  {sim.account_id ? (
                    sim.has_active_subscription ?? sim.has_active_contract ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-400" />
                    )
                  ) : (
                    <Minus className="h-4 w-4 text-muted-foreground/40" />
                  )}
                </TableCell>
                <TableCell>
                  {sim.mongo_transmitter_id ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5">
                            <Radio className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-sm font-mono truncate max-w-[80px]">
                              ...{sim.mongo_transmitter_id.slice(-6)}
                            </span>
                            {sim.match_method && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                {sim.match_method}
                              </Badge>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1 text-xs">
                            <p><strong>Transmitter ID:</strong> {sim.mongo_transmitter_id}</p>
                            {sim.match_method && (
                              <p><strong>Matched by:</strong> {sim.match_method}</p>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {sim.rate_plan || '-'}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatDataSize(sim.usage_ctd_mb)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {(() => {
                    const cost = getSimMonthlyCost(sim.rate_plan, sim.status, volumeTier);
                    return (
                      <span className={cost === null ? 'text-muted-foreground' : cost === 0 ? 'text-muted-foreground' : 'text-emerald-600'}>
                        {formatCost(cost)}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {sim.last_sync_at
                    ? formatDistanceToNow(new Date(sim.last_sync_at), { addSuffix: true })
                    : 'Never'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format ICCID for display (group digits for readability)
 * Example: 89014103211118510720 -> 8901 4103 2111 1851 0720
 */
function formatIccid(iccid: string): string {
  if (!iccid) return '-';
  // Group into 4-digit chunks for readability
  return iccid.replace(/(.{4})/g, '$1 ').trim();
}
